<!--
  @component
  Generates an SVG multi-series line chart. It expects your data to be an array of objects, each with a `values` key that is an array of data objects.
 -->
<script>
  import { getContext } from "svelte";
  import { line, curveLinear } from "d3-shape";
  import { percentFormat } from "$utils/formats.js";

  const { data, xGet, yGet, x, y, custom } = getContext("LayerCake");

  /** @typedef {import('d3-shape').CurveFactory} CurveFactory */
  /**
   * @typedef {Object} Props
   * @property {CurveFactory} [curve] - An optional D3 interpolation function. See [d3-shape](https://github.com/d3/d3-shape#curves) for options. Pass this function in uncalled, i.e. without the open-close parentheses.
   * @property {Object|null} hoveredPoint - either nothing or, if a point has a mouse interaction, a set of year and currentState for filling a tooltip
   */

  /** @type {Props} */
  let { curve = curveLinear, hoveredPoint = $bindable(null) } = $props();

  let path = $derived(line().x($xGet).y($yGet).curve(curve));
  // .defined($y)
</script>

<g class="line-group">
  {#each $data.filter((d) => d.region != "US" && d.region != $custom.currentState) as group, i}
    <path class="path-line other" d={path(group.values)} stroke={$custom.seriesColors[0]}></path>
  {/each}
  <path
    class="path-line us"
    d={path($data.find((d) => d.region == "US").values)}
    stroke={$custom.seriesColors[1]}
    stroke-dasharray={"10 5"}
  ></path>
  <path
    class="path-line current"
    d={path($data.find((d) => d.region == $custom.currentState).values)}
    stroke={$custom.seriesColors[2]}
  ></path>
</g>
<g class="points">
  {#each $data.find((d) => d.region == $custom.currentState).values as point}
    <circle
      r="5"
      cx={$xGet(point)}
      cy={$yGet(point)}
      fill="white"
      stroke={$custom.seriesColors[2]}
      stroke-width="2"
    ></circle>
  {/each}
</g>
<g class="hover-points">
  {#each $data.find((d) => d.region == $custom.currentState).values as point}
    <circle
      class="hoverable-point"
      r="10"
      cx={$xGet(point)}
      cy={$yGet(point)}
      aria-describedby="tooltip"
      fill="transparent"
      onmousemove={(e) => {
        hoveredPoint = {
          x: e.target.cx,
          y: e.target.cy,
          target: e.target,
          state: $custom.currentState,
          year: $x(point),
          value: $y(point)
        };
      }}
      onmouseout={() => {
        hoveredPoint = null;
      }}
      onblur={() => {
        hoveredPoint = null;
      }}
    ></circle>
  {/each}
</g>
<g class="line-label">
  <text
    class="year"
    y={$yGet(
      $data.find((d) => d.region == $custom.currentState).values.find((d) => d.year == 2023)
    ) - 5}
    x={$xGet({ year: 2023 }) + 10}>2023:</text
  >
  <text
    class="underline"
    y={$yGet(
      $data.find((d) => d.region == $custom.currentState).values.find((d) => d.year == 2023)
    ) + 15}
    x={$xGet({ year: 2023 }) + 10}
    >{percentFormat.format(
      $data.find((d) => d.region == $custom.currentState).values.find((d) => d.year == 2023).value
    )}</text
  >
  <text
    class="label"
    y={$yGet(
      $data.find((d) => d.region == $custom.currentState).values.find((d) => d.year == 2023)
    ) + 15}
    x={$xGet({ year: 2023 }) + 10}
    >{percentFormat.format(
      $data.find((d) => d.region == $custom.currentState).values.find((d) => d.year == 2023).value
    )}</text
  >
</g>

<style>
  .path-line {
    fill: none;
    stroke-linejoin: round;
    stroke-linecap: round;
    stroke-width: 1px;
    &.us,
    &.current {
      stroke-width: 3px;
    }
  }
  .underline {
    fill: var(--color-yellow);
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-thickness: 3px;
  }
  .underline,
  .label {
    font-weight: 700;
  }
  .year {
    font-style: italic;
  }
  .hoverable-point {
    cursor: pointer;
  }
</style>
